import React from 'react'
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles'
import Modal from '@material-ui/core/Modal'
import { Button, Card, CardActions, CardContent, Typography } from '@material-ui/core'
import { Cancel, CheckCircle } from '@material-ui/icons'
import axios, { AxiosError } from 'axios'
import { toast } from 'react-toastify'
import SupportDocumentService from '../../../services/SupportDocument'
import { ServerError } from '../../../schemas/Error'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
      height: '100%'
    },
    card: {
      // se adapta a pantallas angostas (16px de margen a cada lado) y hace scroll si no cabe
      width: 'calc(100% - 32px)',
      maxWidth: 480,
      maxHeight: '90vh',
      overflowY: 'auto',
      padding: theme.spacing(2)
    },
    buttonContainer: {
      textAlign: 'right',
      width: '100%',
      paddingTop: theme.spacing(2)
    },
    button: {
      marginLeft: theme.spacing(1)
    },
    message: {
      marginBottom: theme.spacing(2),
      whiteSpace: 'pre-line'
    }
  }),
)

interface Props {
  show: boolean
  cude: string | null
  documentNumber: string | null
  handleModal: (show: boolean) => void
}

const modalText = `¿Desea continuar con la presentación de la nota crédito de documento soporte?

Este documento anulará el documento soporte ya emitido y será presentado ante la DIAN.

Una vez presentado, no podrá ser eliminado ni modificado.`

/** Confirms and presents a support-document credit note for the selected document. */
export default function PresentCreditNoteSupportDocumentModal(props: Props) {
  const classes = useStyles()

  async function confirmPresentCreditNoteSupportDocument() {
    if (props.cude == null) {
      return
    }
    try {
      const supportDocumentService = new SupportDocumentService()
      await supportDocumentService.presentCreditNoteSupportDocument(props.cude)
      props.handleModal(false)
      toast.success('Nota crédito de documento soporte presentada correctamente')
    } catch (error) {
      console.error('Error presenting support-document credit note:', error)
      if (axios.isAxiosError(error)) {
        const serverError = error as AxiosError<ServerError>
        if (serverError?.response?.data?.message) {
          return toast.error(serverError.response.data.message)
        }
      }
      toast.error('Error presentando nota crédito de documento soporte')
    }
  }

  return (
    <Modal
      open={props.show}
      onClose={() => props.handleModal(false)}
      aria-labelledby="present-credit-note-support-document-modal-title"
      aria-describedby="present-credit-note-support-document-modal-description"
    >
      <div className={classes.root}>
        <Card className={classes.card}>
          <CardContent>
            <Typography id="present-credit-note-support-document-modal-title" variant="h6" component="h2" gutterBottom>
              Presentar nota crédito de documento soporte asociada al documento {props.documentNumber ?? ''}
            </Typography>
            <Typography id="present-credit-note-support-document-modal-description" variant="body2" color="textSecondary" className={classes.message}>
              {modalText}
            </Typography>
          </CardContent>
          <CardActions>
            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<CheckCircle />}
                onClick={confirmPresentCreditNoteSupportDocument}
                className={classes.button}
              >
                Continuar
              </Button>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<Cancel />}
                className={classes.button}
                onClick={() => props.handleModal(false)}
              >
                Cancelar
              </Button>
            </div>
          </CardActions>
        </Card>
      </div>
    </Modal>
  )
}
